"use client"

import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Link from "next/link"
import { CommandMenu } from "@/components/nav/CommandMenu"
import {
  SidebarInset,
  Sidebar,
  SidebarProvider,
  SidebarContent,
  SidebarMenuButton,
  SidebarMenu,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/custom-sidebar"
import { Button } from "@/components/ui/button"
import { Toaster } from "sonner"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  FileSearch,
  Settings2,
  SquareTerminal,
  Sparkles,
  LucideIcon,
  FileText,
  PanelLeftOpen,
  PanelLeftClose,
  Home,
  Map,
  Sun,
  PanelRightOpen,
  PanelRightClose,
  FileStack,
  Archive,
  Search,
  BookText,
  BookMarked,
  Network,
  FileJson,
  Shapes,
  EllipsisVertical,
  MessageCircle,
  Bookmark,
  Clock,
  FolderSearch,
  MessageCircleQuestion,
  FolderTree,
  PanelRight,
  PanelLeft,
  ChevronRight,
  Terminal,
} from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils";

import { UserButton } from "@/components/nav/UserButton"
import { NavProjects } from "@/components/nav/nav_projects"
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { FeedbackDialog } from '@/components/feedback/FeedbackDialog';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { AppSidebar } from '@/components/nav/AppSidebar';
import { usePathname } from 'next/navigation';

interface TemplateProps {
  content: ReactNode | null;
  rightSheet: ReactNode | null;
  topMenu: ReactNode | null;
  bottomMenu: ReactNode | null;
}

export function Template({ content = null, rightSheet = null, topMenu = null, bottomMenu = null }: TemplateProps) {

  const [openCommandMenu, setOpenCommandMenu] = React.useState(false)

  const [isScrolled, setIsScrolled] = useState(false);
  
  const pathname = usePathname();

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      scrollContainerRef.current.style.scrollBehavior = 'auto';
    }
  }, [pathname]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Check the scrollTop of the DIV, not the window
      setIsScrolled(container.scrollTop > 0);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  
  const { open } = useSidebar()

  const leftSidebar =
    <AppSidebar />

  let topMenuContainer =
    <div
      className={cn(
        "h-16 flex flex-row justify-center items-center z-2 gap-8 transition transition-all duration-200 ease-linear md:mr-6",
      )}
    >
      {topMenu}
    </div>

  let bottomMenuContainer =
    <div
      className={cn(
        "fixed bottom-0 min-h-16 flex flex-row justify-center items-center z-10 rounded-full gap-8 px-8",
      )}
    >
      {bottomMenu}
    </div>

  if (!topMenu) {
    topMenuContainer = <div></div>;
  }

  if (!bottomMenu) {
    bottomMenuContainer = <div></div>;
  }

  // if (!rightSheet) {
  //   rightSheetContainer = <div className="transition-all duration-300"></div>;
  // }

  const permanentMenuContainer =
    <div className="h-16 flex flex-row justify-start items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarTrigger variant="secondary" size="icon" />
        </TooltipTrigger>
        <TooltipContent>
          {open ? "Hide" : "Show"} sidebar <Kbd>⌘ b</Kbd>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="secondary" size="icon" onClick={() => setOpenCommandMenu(true)}>
            <Terminal />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          Command menu <Kbd>⌘ k</Kbd>
        </TooltipContent>
      </Tooltip>

      <FeedbackDialog />
      <UserButton />
    </div>

  let headerContainer =
    <div 
      className={cn(
        "fixed top-0 left-0 right-0 z-20 w-full min-h-16 md:px-8 flex sm:flex-col md:flex-row justify-between items-center transition-all duration-200",
        isScrolled ? "shadow-[0_0px_24px_rgba(0,0,0,0.07)]" : "shadow-none bg-transparent",
      )}
    >
      <div className="sm:w-full md:w-0 min-h-16 flex flex-row justify-between items-center gap-8">
        <div className="md:hidden">
          {permanentMenuContainer}
        </div>
      </div>
      <div className="w-full flex flex-row justify-start md:justify-end items-center gap-2 sm:px-4 md:p-0 border-t border-b md:border-none">
        {topMenuContainer}
        <div className="hidden md:flex">
          {permanentMenuContainer}
        </div>
      </div>
    </div>

  const sidebarInset =
    <SidebarInset className="sm:px-4 md:px-0 w-full flex flex-col justify-start items-center h-screen overflow-hidden">
      <div
        id="main-content-area"
        ref={scrollContainerRef}
        className={cn(
          "relative w-full overflow-y-auto scroll-smooth no-sidebar",
          "transition-all duration-200 ease-linear",
          "mt-32 md:mt-16",
          bottomMenu ? "mb-16" : "mb-0",
          rightSheet && "pr-[196px]"
        )}
        style={{ scrollbarWidth: 'none' }}
      >
        <div className="w-full max-w-3xl mx-auto flex flex-col items-center">
          {content}
        </div>
      </div>

      {bottomMenuContainer}
    </SidebarInset>

  return (
    <div className="w-full bg-background">
      <div className="relative flex h-screen w-full overflow-hidden">
        <div className="fixed top-0 left-8 h-16 z-200 flex flex-row justify-start items-center font-semibold text-2xl">
          LawLibrary<span className="text-primary">.ai</span>
        </div>
        {leftSidebar}
        {headerContainer}
        {sidebarInset}
        {/* {rightSheet} */}
      </div>
      <CommandMenu open={openCommandMenu} setOpen={setOpenCommandMenu} />
    </div>
  )
}
